/* eslint-disable @typescript-eslint/no-explicit-any */
import { type AnonymizationAttribute } from './models/anonymization_attribute';
import { ModalityAggregationRule } from './models/anonymization_rule';

export class DICOMUtils {
    private static _tagNames: Map<string, string> = new Map();
    private static _valueRepresentations: { [id: string]: string } = {
        FileMetaInformationVersion: 'OB',
        MediaStorageSOPClassUID: 'UI',
        MediaStorageSOPInstanceUID: 'UI',
        TransferSyntaxUID: 'UI',
        ImplementationClassUID: 'UI',
        ImplementationVersionName: 'SH',
    };

    /**
     * Get the attribute value by the rule ModalityAggregationRule or empty string if it is not found
     * @param dataset
     * @param rule
     * @returns
     */
    public static attributeByRule(
        dataset: any,
        rule: ModalityAggregationRule
    ): string {
        const modality = (dataset.Modality || 'CT').toLowerCase();
        const attributeRule = rule[modality] || rule['other'];
        return DICOMUtils.matchingAttributes(dataset, attributeRule)[0] || '';
    }

    /**
     * Get the value representation (VR) of the attribute by name
     * @param attribute
     * @returns
     */
    public static getValueRepresentation(attribute: string): string {
        if (attribute in this._valueRepresentations) {
            return this._valueRepresentations[attribute];
        }

        return 'UI';
    }

    /**
     * Get tag value for metainfo tag
     * @param tag
     */
    public static metaTagValue(value: any): string {
        if (!value) {
            return value;
        }
        if (typeof value === 'object' && 'Value' in value) {
            return value.Value.length > 0 ? value.Value[0] : '';
        }
        if (
            typeof value === 'object' &&
            Array.isArray(value) &&
            value.length > 0
        ) {
            if (typeof value[0] === 'object' && 'Alphabetic' in value[0]) {
                return value[0].Alphabetic;
            }
        }

        return value;
    }

    /**
     * Returns the list of all attributes matched by the rule as a flat list
     * If certain DICOM tag has a matching attribute and subrule, it will returns the attribute and the list of attributes from the subrule
     * @param dataset
     * @param rule
     * @returns
     */
    public static matchingAttributes(
        dataset: any,
        rule: AnonymizationAttribute
    ): string[] {
        // Pick all available attributes
        if (rule.attributes) {
            for (const attribute of rule.attributes) {
                if (attribute) {
                    const element = dataset[attribute];
                    if (element) {
                        return [element];
                    }
                }
            }
        }

        // Recurse into the rule if no attributes found
        if (rule.rule) {
            for (const subrule of rule.rule) {
                const tag = subrule.name;
                if (tag) {
                    const subset = dataset[tag];
                    const results: string[] = [];

                    if (subset) {
                        for (const set of subset) {
                            results.push(
                                ...DICOMUtils.matchingAttributes(set, subrule)
                            );
                        }
                    }

                    return results;
                }
            }
        }

        // No attributes found, normally should not happen
        return [''];
    }
}
